import React, { FC, PropsWithChildren, createContext, useState, useCallback, useRef, useEffect } from 'react'
import useRouter from '../hooks/useRouter'

export interface IPendingNavigation {
  routeName: string
  routeParams?: Record<string, any>
}

export interface IUnsavedChangesContext {
  // Register a component as having unsaved changes
  registerDirty: (id: string) => void
  // Unregister a component (no longer has unsaved changes or unmounting)
  unregisterDirty: (id: string) => void
  // Check if any component has unsaved changes
  hasDirtyComponents: () => boolean
  // For the dialog - current pending navigation to confirm/reject
  pendingNavigation: IPendingNavigation | null
  // Confirm leaving (proceed with navigation)
  confirmLeave: () => void
  // Cancel leaving (stay on page)
  cancelLeave: () => void
}

export const UnsavedChangesContext = createContext<IUnsavedChangesContext>({
  registerDirty: () => {},
  unregisterDirty: () => {},
  hasDirtyComponents: () => false,
  pendingNavigation: null,
  confirmLeave: () => {},
  cancelLeave: () => {},
})

export const UnsavedChangesContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter()
  const dirtyComponentsRef = useRef(new Set<string>())
  const [pendingNavigation, setPendingNavigation] = useState<IPendingNavigation | null>(null)

  // Store the original navigate function
  const originalNavigateRef = useRef<typeof router.navigate | null>(null)

  // Handle beforeunload for browser navigation
  const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
    if (dirtyComponentsRef.current.size > 0) {
      e.preventDefault()
      e.returnValue = '' // Required for Chrome
      return '' // Required for some browsers
    }
  }, [])

  const updateBeforeUnloadListener = useCallback(() => {
    if (dirtyComponentsRef.current.size > 0) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [handleBeforeUnload])

  const registerDirty = useCallback((id: string) => {
    dirtyComponentsRef.current.add(id)
    updateBeforeUnloadListener()
  }, [updateBeforeUnloadListener])

  const unregisterDirty = useCallback((id: string) => {
    dirtyComponentsRef.current.delete(id)
    updateBeforeUnloadListener()
  }, [updateBeforeUnloadListener])

  const hasDirtyComponents = useCallback(() => {
    return dirtyComponentsRef.current.size > 0
  }, [])

  const confirmLeave = useCallback(() => {
    if (pendingNavigation && originalNavigateRef.current) {
      // Clear dirty state
      dirtyComponentsRef.current.clear()
      window.removeEventListener('beforeunload', handleBeforeUnload)

      // Store the pending navigation before clearing state
      const { routeName, routeParams } = pendingNavigation
      setPendingNavigation(null)

      // Navigate using original function
      originalNavigateRef.current(routeName, routeParams)
    }
  }, [pendingNavigation, handleBeforeUnload])

  const cancelLeave = useCallback(() => {
    setPendingNavigation(null)
  }, [])

  // Set up the navigation blocker
  useEffect(() => {
    // Store the original navigate function
    originalNavigateRef.current = router.navigate

    // Set up the navigation blocker
    router.setNavigationBlocker?.((routeName: string, routeParams?: Record<string, any>) => {
      if (dirtyComponentsRef.current.size > 0) {
        // Block navigation and show dialog
        setPendingNavigation({ routeName, routeParams })
        return false
      }
      return true
    })

    return () => {
      // Clean up
      router.setNavigationBlocker?.(null)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [router, handleBeforeUnload])

  const contextValue: IUnsavedChangesContext = {
    registerDirty,
    unregisterDirty,
    hasDirtyComponents,
    pendingNavigation,
    confirmLeave,
    cancelLeave,
  }

  return (
    <UnsavedChangesContext.Provider value={contextValue}>
      {children}
    </UnsavedChangesContext.Provider>
  )
}

export default UnsavedChangesContext
